import { inject, Injectable } from '@angular/core';
import { ShapeLocationState } from '../../../cross-team-shared/annotation/shared/state/shape-location-state';
import { addPoints } from '../../../shared/misc-utilities/point-utils';
import { shellConnectorToken } from '../../../team-apis/core/shell-connector';
import {
  Point2D,
  VectorTaggingShapeType,
} from '../../../team-apis/otterdam/answers/shape-typings';
import { getShapeHelper } from '../shape-helpers/get-shape-helper';
import { AbstractSensorContentStore2D } from '../state/abstract-sensor-content-store-2d';
import { LocationHelper } from './location-helper';

@Injectable()
export class ViewBoxPaddingService {
  private store = inject(AbstractSensorContentStore2D);
  private shellConnector = inject(shellConnectorToken);

  updatePadding() {
    const newPadding = this.getMinimumPaddingForFocusedShape();

    const { viewBoxPadding } = this.store.state();
    if (
      viewBoxPadding &&
      newPadding &&
      Math.abs(viewBoxPadding / newPadding - 1) < 0.02
    ) {
      // Recalculating the padding after switching shape to which we applied the
      // same padding will result in tiny differences, which causes the viewBox
      // to zoom out on each selection change. Ignoring tiny changes fixes this.
      return;
    }

    this.store('viewBoxPadding').set(newPadding);
    this.shellConnector.propagateViewBoxPaddingUpdate(newPadding);
  }

  addPaddingToBoundingPoints(
    boundingPoints: [Point2D, Point2D],
    padding = this.store.state().viewBoxPadding ?? 1,
  ): [Point2D, Point2D] {
    const width = boundingPoints[1][0] - boundingPoints[0][0];
    const height = boundingPoints[1][1] - boundingPoints[0][1];
    const paddedWidth = padding * width;
    const paddedHeight = padding * height;
    const xPadding = (paddedWidth - width) / 2;
    const yPadding = (paddedHeight - height) / 2;

    return [
      addPoints(boundingPoints[0], [-xPadding, -yPadding]),
      addPoints(boundingPoints[1], [xPadding, yPadding]),
    ];
  }

  private getMinimumPaddingForFocusedShape() {
    const focusedShapeStore = this.store.getFocusedShapeStore();

    if (!focusedShapeStore) {
      return undefined;
    }

    const selectedShape = focusedShapeStore.state();
    const location = new LocationHelper(this.store).determineLocation(
      selectedShape.key_locations,
      this.store.state().frameNumber,
    );

    if (location.points.length <= 1) {
      return undefined;
    }

    return this.calculatePadding(selectedShape.type, location);
  }

  private calculatePadding(
    shapeType: VectorTaggingShapeType,
    location: ShapeLocationState<Point2D>,
  ) {
    const [_x, _y, viewBoxWidth, viewBoxHeight] = this.store.state().viewBox;
    const [topLeft, bottomRight] = getShapeHelper(shapeType).getBoundingPoints(
      location.points,
    );
    const shapeWidth = bottomRight[0] - topLeft[0];
    const shapeHeight = bottomRight[1] - topLeft[1];

    return Math.min(viewBoxWidth / shapeWidth, viewBoxHeight / shapeHeight);
  }
}
